/**
 * Filter parsing, serialization and validation.
 *
 * One implementation serves BOTH transports:
 *   - URL search params (shareable, refresh-safe, hand-editable)
 *   - saved-view JSONB  (persisted, may predate the current filter shape)
 *
 * Both are untrusted input. A hand-edited URL or a saved view written before a
 * filter existed must degrade — drop the bad key, keep the rest — never throw
 * and never blank the page. This mirrors `parseToggleFilter` in the builder's
 * `useUrlLocationFilters`, which coerces anything unexpected back to a default.
 *
 * Defaults are STRIPPED on serialize so URLs stay short and self-describing.
 * The one exception is `includeTest`, whose default (false) is meaningful: see
 * below.
 */

import type {
  BookingFilters,
  DateRangeFilter,
  QuoteFilters,
  TableSort,
} from '../types/reporting'
import {
  BOOKING_SERVICE_TYPE_OPTIONS,
  BOOKING_STATUS_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
  QUOTE_SERVICE_TYPE_OPTIONS,
  QUOTE_STATUS_OPTIONS,
} from '../types/reporting'

// =============================================================================
// Primitive coercion
// =============================================================================

/** A record of raw, untrusted values (URL params or parsed JSONB). */
export type RawFilterInput = Readonly<Record<string, unknown>>

function asString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

/**
 * Finite numbers only. `NaN`/`Infinity` would serialize into a URL as garbage
 * and PostgREST would reject the filter.
 */
function asNumber(value: unknown): number | undefined {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  if (trimmed.length === 0) return undefined
  const n = Number(trimmed)
  return Number.isFinite(n) ? n : undefined
}

/** Only literal true/false — anything else is "not set", never a silent false. */
function asBoolean(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value
  if (value === 'true') return true
  if (value === 'false') return false
  return undefined
}

/** An ISO timestamp we can actually parse. */
function asIsoDate(value: unknown): string | undefined {
  const str = asString(value)
  if (str === undefined) return undefined
  const parsed = new Date(str)
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString()
}

/**
 * Coerce to a de-duplicated list restricted to `allowed`.
 *
 * Accepts an array (JSONB) or a comma-separated string (URL). Values outside
 * `allowed` are dropped rather than passed through: an unknown status would
 * make PostgREST return zero rows, which reads as "no data" rather than "bad
 * filter".
 */
function asEnumList<T extends string>(
  value: unknown,
  allowed: readonly { readonly value: T }[]
): readonly T[] | undefined {
  const permitted = new Set<string>(allowed.map((o) => o.value))

  const candidates: unknown[] = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(',')
      : []

  const out: T[] = []
  for (const candidate of candidates) {
    const str = asString(candidate)
    if (str !== undefined && permitted.has(str) && !out.includes(str as T)) {
      out.push(str as T)
    }
  }
  return out.length > 0 ? out : undefined
}

/** Free-form string list (UTM values, vehicle UUIDs) — no allow-list to check. */
function asStringList(value: unknown): readonly string[] | undefined {
  const candidates: unknown[] = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(',')
      : []

  const out: string[] = []
  for (const candidate of candidates) {
    const str = asString(candidate)
    if (str !== undefined && !out.includes(str)) out.push(str)
  }
  return out.length > 0 ? out : undefined
}

/**
 * Parse a `from`/`to` pair, normalising an inverted range by swapping.
 *
 * Swapping rather than discarding: a reversed range is almost always a typo or a
 * mis-drag, and returning zero rows would look like "no bookings that month".
 */
function asDateRange(rawFrom: unknown, rawTo: unknown): DateRangeFilter | undefined {
  const from = asIsoDate(rawFrom)
  const to = asIsoDate(rawTo)

  if (from === undefined && to === undefined) return undefined
  if (from !== undefined && to !== undefined && new Date(from) > new Date(to)) {
    return { from: to, to: from }
  }
  return { ...(from !== undefined && { from }), ...(to !== undefined && { to }) }
}

/** Normalise an inverted numeric range the same way. */
function orderedRange(
  min: number | undefined,
  max: number | undefined
): { readonly min?: number; readonly max?: number } {
  if (min !== undefined && max !== undefined && min > max) return { min: max, max: min }
  return { ...(min !== undefined && { min }), ...(max !== undefined && { max }) }
}

// =============================================================================
// Sort
// =============================================================================

export function parseSort<F extends string>(
  value: unknown,
  allowedFields: readonly F[],
  fallback: TableSort<F>
): TableSort<F> {
  // Accepts { field, direction } (JSONB) or "field:direction" (URL).
  let field: unknown
  let direction: unknown

  if (typeof value === 'string') {
    const [f, d] = value.split(':')
    field = f
    direction = d
  } else if (value !== null && typeof value === 'object') {
    field = (value as Record<string, unknown>).field
    direction = (value as Record<string, unknown>).direction
  }

  const validField = allowedFields.find((f) => f === field)
  if (validField === undefined) return fallback

  return {
    field: validField,
    direction: direction === 'asc' ? 'asc' : 'desc',
  }
}

export function serializeSort<F extends string>(sort: TableSort<F>): string {
  return `${sort.field}:${sort.direction}`
}

// =============================================================================
// Bookings
// =============================================================================

export function parseBookingFilters(raw: RawFilterInput): BookingFilters {
  const amount = orderedRange(asNumber(raw.amountMin), asNumber(raw.amountMax))
  const passengers = orderedRange(asNumber(raw.passengerMin), asNumber(raw.passengerMax))

  const pickupDate = asDateRange(raw.pickupFrom ?? raw.pickupDateFrom, raw.pickupTo ?? raw.pickupDateTo)
  const createdDate = asDateRange(raw.createdFrom ?? raw.createdDateFrom, raw.createdTo ?? raw.createdDateTo)

  return {
    ...(asString(raw.search) !== undefined && { search: asString(raw.search) }),
    ...(pickupDate !== undefined && { pickupDate }),
    ...(createdDate !== undefined && { createdDate }),

    ...(asEnumList(raw.bookingStatus, BOOKING_STATUS_OPTIONS) !== undefined && {
      bookingStatus: asEnumList(raw.bookingStatus, BOOKING_STATUS_OPTIONS),
    }),
    ...(asEnumList(raw.paymentStatus, PAYMENT_STATUS_OPTIONS) !== undefined && {
      paymentStatus: asEnumList(raw.paymentStatus, PAYMENT_STATUS_OPTIONS),
    }),

    ...(amount.min !== undefined && { amountMin: amount.min }),
    ...(amount.max !== undefined && { amountMax: amount.max }),
    ...(asBoolean(raw.hasBalanceDue) === true && { hasBalanceDue: true }),

    ...(asEnumList(raw.serviceType, BOOKING_SERVICE_TYPE_OPTIONS) !== undefined && {
      serviceType: asEnumList(raw.serviceType, BOOKING_SERVICE_TYPE_OPTIONS),
    }),
    ...(asStringList(raw.vehicleIds) !== undefined && {
      vehicleIds: asStringList(raw.vehicleIds),
    }),
    ...(passengers.min !== undefined && { passengerMin: passengers.min }),
    ...(passengers.max !== undefined && { passengerMax: passengers.max }),
    ...(asBoolean(raw.roundTrip) !== undefined && { roundTrip: asBoolean(raw.roundTrip) }),
    ...(asBoolean(raw.airportPickup) !== undefined && {
      airportPickup: asBoolean(raw.airportPickup),
    }),
    ...(asBoolean(raw.airportDropoff) !== undefined && {
      airportDropoff: asBoolean(raw.airportDropoff),
    }),

    // Only ever true when explicitly requested. Absent, malformed or "false"
    // all mean "exclude test bookings" — the safe default for a revenue report.
    ...(asBoolean(raw.includeTest) === true && { includeTest: true }),

    ...(asStringList(raw.utmSource) !== undefined && { utmSource: asStringList(raw.utmSource) }),
    ...(asStringList(raw.utmMedium) !== undefined && { utmMedium: asStringList(raw.utmMedium) }),
    ...(asStringList(raw.utmCampaign) !== undefined && {
      utmCampaign: asStringList(raw.utmCampaign),
    }),
  }
}

export function parseQuoteFilters(raw: RawFilterInput): QuoteFilters {
  const amount = orderedRange(asNumber(raw.amountMin), asNumber(raw.amountMax))
  const passengers = orderedRange(asNumber(raw.passengerMin), asNumber(raw.passengerMax))

  const pickupDate = asDateRange(raw.pickupFrom, raw.pickupTo)
  const createdDate = asDateRange(raw.createdFrom, raw.createdTo)

  return {
    ...(asString(raw.search) !== undefined && { search: asString(raw.search) }),
    ...(pickupDate !== undefined && { pickupDate }),
    ...(createdDate !== undefined && { createdDate }),

    ...(asEnumList(raw.status, QUOTE_STATUS_OPTIONS) !== undefined && {
      status: asEnumList(raw.status, QUOTE_STATUS_OPTIONS),
    }),

    ...(amount.min !== undefined && { amountMin: amount.min }),
    ...(amount.max !== undefined && { amountMax: amount.max }),

    ...(asEnumList(raw.serviceType, QUOTE_SERVICE_TYPE_OPTIONS) !== undefined && {
      serviceType: asEnumList(raw.serviceType, QUOTE_SERVICE_TYPE_OPTIONS),
    }),
    ...(asStringList(raw.vehicleIds) !== undefined && {
      vehicleIds: asStringList(raw.vehicleIds),
    }),
    ...(passengers.min !== undefined && { passengerMin: passengers.min }),
    ...(passengers.max !== undefined && { passengerMax: passengers.max }),
    ...(asBoolean(raw.roundTrip) !== undefined && { roundTrip: asBoolean(raw.roundTrip) }),
    ...(asBoolean(raw.airportPickup) !== undefined && {
      airportPickup: asBoolean(raw.airportPickup),
    }),
    ...(asBoolean(raw.airportDropoff) !== undefined && {
      airportDropoff: asBoolean(raw.airportDropoff),
    }),

    ...(asBoolean(raw.isOperatorPriced) !== undefined && {
      isOperatorPriced: asBoolean(raw.isOperatorPriced),
    }),
    ...(asBoolean(raw.isFlatRate) !== undefined && { isFlatRate: asBoolean(raw.isFlatRate) }),
    ...(asBoolean(raw.hasDiscount) !== undefined && { hasDiscount: asBoolean(raw.hasDiscount) }),

    ...(asStringList(raw.utmSource) !== undefined && { utmSource: asStringList(raw.utmSource) }),
    ...(asStringList(raw.utmMedium) !== undefined && { utmMedium: asStringList(raw.utmMedium) }),
    ...(asStringList(raw.utmCampaign) !== undefined && {
      utmCampaign: asStringList(raw.utmCampaign),
    }),
  }
}

// =============================================================================
// Serialization (URL params / JSONB)
// =============================================================================

/**
 * Flatten filters to a string map for `URLSearchParams`.
 *
 * Keys whose value is absent are omitted entirely, so a URL only ever names
 * filters that are actually active — which is also what makes the round-trip
 * through `parseBookingFilters` lossless.
 */
export function serializeFilters(
  filters: BookingFilters | QuoteFilters
): Readonly<Record<string, string>> {
  const out: Record<string, string> = {}

  const put = (key: string, value: string | number | boolean | undefined): void => {
    if (value === undefined) return
    out[key] = String(value)
  }
  const putList = (key: string, value: readonly string[] | undefined): void => {
    if (value === undefined || value.length === 0) return
    out[key] = value.join(',')
  }

  const f = filters as BookingFilters & QuoteFilters

  put('search', f.search)

  put('pickupFrom', f.pickupDate?.from)
  put('pickupTo', f.pickupDate?.to)
  put('createdFrom', f.createdDate?.from)
  put('createdTo', f.createdDate?.to)

  putList('bookingStatus', f.bookingStatus)
  putList('paymentStatus', f.paymentStatus)
  putList('status', f.status)

  put('amountMin', f.amountMin)
  put('amountMax', f.amountMax)
  if (f.hasBalanceDue === true) put('hasBalanceDue', true)

  putList('serviceType', f.serviceType)
  putList('vehicleIds', f.vehicleIds)
  put('passengerMin', f.passengerMin)
  put('passengerMax', f.passengerMax)
  put('roundTrip', f.roundTrip)
  put('airportPickup', f.airportPickup)
  put('airportDropoff', f.airportDropoff)

  // Only serialized when true — false is the default and stays out of the URL.
  if (f.includeTest === true) put('includeTest', true)

  put('isOperatorPriced', f.isOperatorPriced)
  put('isFlatRate', f.isFlatRate)
  put('hasDiscount', f.hasDiscount)

  putList('utmSource', f.utmSource)
  putList('utmMedium', f.utmMedium)
  putList('utmCampaign', f.utmCampaign)

  return out
}

/** Every key `serializeFilters` may emit — used to clear filters from a URL. */
export const FILTER_PARAM_KEYS: readonly string[] = [
  'search',
  'pickupFrom',
  'pickupTo',
  'createdFrom',
  'createdTo',
  'bookingStatus',
  'paymentStatus',
  'status',
  'amountMin',
  'amountMax',
  'hasBalanceDue',
  'serviceType',
  'vehicleIds',
  'passengerMin',
  'passengerMax',
  'roundTrip',
  'airportPickup',
  'airportDropoff',
  'includeTest',
  'isOperatorPriced',
  'isFlatRate',
  'hasDiscount',
  'utmSource',
  'utmMedium',
  'utmCampaign',
]

/** True when no filter is active (drives "Clear all" visibility and chips). */
export function hasActiveFilters(filters: BookingFilters | QuoteFilters): boolean {
  return Object.keys(serializeFilters(filters)).length > 0
}

/**
 * Count active filters for the "More filters (n)" badge.
 * A date range counts once even though it serializes to two params.
 */
export function countActiveFilters(filters: BookingFilters | QuoteFilters): number {
  const serialized = serializeFilters(filters)
  const keys = Object.keys(serialized)

  const pairs: readonly (readonly [string, string])[] = [
    ['pickupFrom', 'pickupTo'],
    ['createdFrom', 'createdTo'],
    ['amountMin', 'amountMax'],
    ['passengerMin', 'passengerMax'],
  ]

  let count = keys.length
  for (const [a, b] of pairs) {
    if (keys.includes(a) && keys.includes(b)) count -= 1
  }
  return count
}
